import React, { memo, useContext, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { SocketTradeData, TradeData } from "@shared/types";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { SocketPnlContext } from "@shared/features/dashboard/SocketPnl.context";
import { DISPLAY_CURRENCIES } from "common/constants";
import { PhoneTradeLadder } from "features/trades/NewLook/PhoneTradeRow.tsx";
import {
  getHedgeLongShare,
  getTradeRowLadder,
} from "common/NewLook/priceLadder.ts";
import IconWithSource from "features/trades/common/IconWithSource.tsx";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { AccountObjectType } from "@shared/features/settings/types";
import useTradeCard from "features/dashboard/common/useTradeCard";
import {
  useLadderPriceFormat,
  useTradeDayPriceChange,
} from "features/trades/NewLook/useTradeLadder.ts";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_FIGURE_WEIGHT,
  newLookPhoneType,
} from "common/NewLook/tokens.ts";
import { DASHBOARD_TRADE_CARD_ID, DASHBOARD_TRADE_GRID_ID } from "testIds";
import {
  PhoneCard,
  PhoneLoadingRow,
  PhoneMetaPair,
  PhonePill,
  PhoneRowDivider,
  PhoneSkeleton,
} from "./PhoneParts";
import { phoneCompactMoney } from "./PhoneSummary";

interface PhoneTradesListProps {
  trades: SocketTradeData[];
  allTrades: TradeData[] | undefined;
  loading: boolean;
  maxRows?: number;
}

/** The phone dashboard's open trades: one card, a row and a ladder per trade. */
const PhoneTradesList = ({
  trades,
  allTrades,
  loading,
  maxRows = 3,
}: PhoneTradesListProps) => (
  <PhoneCard data-testid={DASHBOARD_TRADE_GRID_ID}>
    {loading && !trades.length
      ? [0, 1, 2].map((index) => (
          <PhoneLoadingRow key={index}>
            <PhoneSkeleton $width={34} $height={34} $radius={17} />
            <Names>
              <PhoneSkeleton $width={96} $height={12} />
              <PhoneSkeleton $width={140} $height={10} />
            </Names>
            <PhoneSkeleton $width={56} $height={14} />
          </PhoneLoadingRow>
        ))
      : trades
          .slice(0, maxRows)
          .map((trade) => (
            <PhoneTradeRow key={trade.id} trade={trade} allTrades={allTrades} />
          ))}
  </PhoneCard>
);

export default memo(PhoneTradesList);

interface PhoneTradeRowProps {
  trade: SocketTradeData;
  allTrades: TradeData[] | undefined;
}

const PhoneTradeRow = memo(({ trade, allTrades }: PhoneTradeRowProps) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const { socketPnlValue } = useContext(SocketPnlContext) || {};
  const live = socketPnlValue?.[trade.id];
  const tradeData = useMemo(
    () => (live ? { ...trade, ...live } : trade) as TradeData,
    [trade, live]
  );
  const { openTradeExtraDetails, isTradeClosed } = useTradeCard({ tradeData });
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isBtc = displayCurrency === DISPLAY_CURRENCIES.BTC;
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const symbol = isBtc ? "₿" : "$";

  const hedgeLongShare = getHedgeLongShare(tradeData, allTrades);
  const model = useMemo(
    () => getTradeRowLadder(tradeData, true, hedgeLongShare),
    [tradeData, hedgeLongShare]
  );
  const formatPrice = useLadderPriceFormat(
    tradeData.symbol,
    tradeData.exchange
  );
  const priceChange = useTradeDayPriceChange(tradeData);

  const pnl = isBtc ? tradeData.btc_amount_change : tradeData.usd_amount_change;
  const pnlPercent =
    tradeData.graph_data && "profit" in tradeData.graph_data
      ? Number(tradeData.graph_data.profit)
      : undefined;
  const amount = isBtc
    ? tradeData.allocated_amount_btc
    : tradeData.allocated_amount_usd;
  const signed = (value: number) =>
    `${
      value < 0 ? "−" : value > 0 ? "+" : ""
    }${symbol}${getLocaleStringWithPrecision(Math.abs(value), precision)}`;
  const isShort = tradeData["Long/Short"] === "Short";
  const side = [
    tradeData["Long/Short"],
    tradeData.leverage ? `${tradeData.leverage}x` : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Row
      data-testid={DASHBOARD_TRADE_CARD_ID}
      $closed={!!isTradeClosed}
      $clickable={!!openTradeExtraDetails}
      onClick={openTradeExtraDetails}
    >
      <Line>
        <IconWithSource
          symbol={tradeData.symbol}
          sourceType={tradeData.source_type}
          source={tradeData.group}
          mainIconSize={34}
          style={ICON_STYLE}
        />
        <Names>
          <SymbolLine>
            <InactiveAccountIcon
              accountId={tradeData.client_id}
              objectType={AccountObjectType.TRADE}
            >
              <SymbolText>{tradeData.symbol}</SymbolText>
            </InactiveAccountIcon>
            {side && (
              <PhonePill $tone={isShort ? "red" : "green"}>{side}</PhonePill>
            )}
          </SymbolLine>
          <PhoneMetaPair
            lead={tradeData.account}
            tail={`${symbol}${getLocaleStringWithPrecision(
              amount || 0,
              precision
            )}`}
            compactTail={phoneCompactMoney(amount || 0, symbol)}
          />
        </Names>
        <Amounts>
          <Pnl style={{ color: directionColor(tokens, pnl) }}>
            {signed(pnl || 0)}
          </Pnl>
          {pnlPercent !== undefined && (
            <Percent style={{ color: directionColor(tokens, pnlPercent) }}>
              {pnlPercent > 0 ? "+" : pnlPercent < 0 ? "−" : ""}
              {Math.abs(pnlPercent).toFixed(2)}%
            </Percent>
          )}
        </Amounts>
      </Line>
      <PhoneTradeLadder
        trade={tradeData}
        model={model}
        formatPrice={formatPrice}
        priceChange={priceChange}
      />
    </Row>
  );
});

const ICON_STYLE = { flex: "none", marginRight: 4 };

const Row = styled.div<{ $closed: boolean; $clickable: boolean }>`
  padding: 12px 0;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  opacity: ${(p) => (p.$closed ? 0.45 : 1)};
  transition: opacity 400ms;
  ${PhoneRowDivider};
`;
const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const Names = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;
const SymbolLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 6px;
  min-width: 0;
  /* One line: the pair keeps its full name, and a side pill that no longer
     fits wraps out of sight instead of squeezing it. */
  height: 20px;
  overflow: hidden;
`;
const SymbolText = styled.span`
  ${newLookPhoneType.cardTitle};
  display: block;
  min-width: 0;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Amounts = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const Pnl = styled.span`
  ${newLookPhoneType.rowFigure};
  white-space: nowrap;
`;
const Percent = styled.span`
  ${newLookPhoneType.meta};
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
`;
